// src/pages/auth/LoginPage.tsx
import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Button from '../../components/base/Button'
import Input from '../../components/base/Input'
import Card from '../../components/base/Card'
import RotatingMessage from './RotateMessage'
import TurnstileGate from '../../components/TurnstileGate'
import type { TurnstileGateHandle } from '../../components/TurnstileGate'
import { LS_EMAIL_KEY } from '../../lib/constants'

export default function LoginPage() {
  const navigate = useNavigate()
  const gateRef = useRef<TurnstileGateHandle>(null)
  const [token, setToken] = useState<string | null>(null)
  const [rememberMe, setRememberMe] = useState(false)
  const [params] = useSearchParams()
  const verified = params.get('verified')

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ✅ 1) 최초 로딩 시 localStorage 이메일 불러오기
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_EMAIL_KEY)
      if (saved) {
        setFormData((prev) => ({ ...prev, email: saved }))
      }
    } catch {
      // localStorage 사용 불가 환경이면 무시
    }
  }, [])

  // ✅ 2) 이메일 변경 시 localStorage에 자동 저장
  useEffect(() => {
    try {
      const email = formData.email.trim().toLowerCase()
      if (email) localStorage.setItem(LS_EMAIL_KEY, email)
      // 이메일을 비우면 저장값도 제거하고 싶다면:
      // else localStorage.removeItem(LS_EMAIL_KEY)
    } catch {
      // ignore
    }
  }, [formData.email])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // 간단한 프론트 검증
    if (!formData.email.trim() || !formData.password) {
      setError('이메일과 비밀번호를 입력해주세요.')
      return
    }

    if (!token) {
      setError('봇 검증을 완료해 주세요.')
      gateRef.current?.show() // (선택) 혹시 숨겨져 있으면 보여주기
      return
    }

    setLoading(true)
    setError(null)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include', // 쿠키 받기/보내기
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          turnstileToken: token,
          rememberMe
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        let msg = '로그인에 실패했습니다. 이메일 또는 비밀번호를 확인해주세요.'
        if (res.status === 403 && data?.error === 'email_not_verified') {
          // ✅ 팝업/토스트/모달 등으로 안내
          msg = '이메일 인증이 필요합니다. 가입한 이메일의 인증 링크를 확인해 주세요.'
          setError(msg)
          // ✅ 대시보드 이동 X
          return
        }
        gateRef.current?.reset() // 다시 체크 요구
        try {
          const data = await res.json()
          if (data?.error) msg = data.error
        } catch {
          // json 파싱 실패 시 기본 메시지 사용
        }
        setError(msg)
        return
      }

      const data = await res.json()

      // 필요하면 유저 정보/토큰을 저장 (예: localStorage)
      // localStorage.setItem("user", JSON.stringify(data));

      // 로그인 성공 → 대시보드로 이동
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      setError('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <i className="ri-cloud-line text-white text-2xl"></i>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">Sense365 Cloud</h2>
        <RotatingMessage />
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Email 주소"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              icon="ri-mail-line"
              placeholder="이메일을 입력하세요"
              required
            />

            <Input
              label="비밀번호"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              icon="ri-lock-line"
              placeholder="비밀번호를 입력하세요"
              required
            />

            <TurnstileGate ref={gateRef} action="login" onTokenChange={setToken} />

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  기억하기
                </label>
              </div>

              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-500 font-medium"
              >
                비밀번호를 잊으셨나요?
              </Link>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            {verified === 'success' && (
              <p className="text-sm text-green-600">
                이메일 인증이 완료되었습니다. 로그인해주세요.
              </p>
            )}

            <Button type="submit" className="w-full" loading={loading}>
              로그인
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Sense365 Cloud에 가입하셨나요?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/signup"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                회원 가입
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
